import type { TwoRoomConfig } from "../../engines/types";
import { useT } from "../../i18n";

interface TwoRoomAdvancedPickersProps {
	config: TwoRoomConfig;
	onChange: (config: Partial<TwoRoomConfig>) => void;
}

function RadioOption<TValue extends string>({
	name,
	value,
	checked,
	label,
	onChange,
}: {
	name: string;
	value: TValue;
	checked: boolean;
	label: string;
	onChange: (value: TValue) => void;
}) {
	return (
		<label className="two-room-radio-option">
			<input
				type="radio"
				name={name}
				value={value}
				checked={checked}
				onChange={() => onChange(value)}
			/>
			<span>{label}</span>
		</label>
	);
}

export function TwoRoomAdvancedPickers({
	config,
	onChange,
}: TwoRoomAdvancedPickersProps) {
	const t = useT();

	return (
		<div className="two-room-advanced-panel">
			<fieldset className="two-room-advanced-field">
				<legend>{t.twoRoom_entryWallLabel}</legend>
				<div className="two-room-radio-group">
					<RadioOption
						name="two-room-entry-wall"
						value="short"
						checked={config.entryWall === "short"}
						label={t.twoRoom_entryWallShort}
						onChange={(entryWall) => onChange({ entryWall })}
					/>
					<RadioOption
						name="two-room-entry-wall"
						value="long"
						checked={config.entryWall === "long"}
						label={t.twoRoom_entryWallLong}
						onChange={(entryWall) => onChange({ entryWall })}
					/>
				</div>
			</fieldset>

			<fieldset className="two-room-advanced-field">
				<legend>{t.twoRoom_glazingStyleLabel}</legend>
				<div className="two-room-radio-group">
					<RadioOption
						name="two-room-glazing-style"
						value="standard"
						checked={config.glazingStyle === "standard"}
						label={t.twoRoom_glazingStandard}
						onChange={(glazingStyle) => onChange({ glazingStyle })}
					/>
					<RadioOption
						name="two-room-glazing-style"
						value="panorama"
						checked={config.glazingStyle === "panorama"}
						label={t.twoRoom_glazingPanorama}
						onChange={(glazingStyle) => onChange({ glazingStyle })}
					/>
				</div>
			</fieldset>

			{config.hasRelaxRoom && (
				<fieldset className="two-room-advanced-field">
					<legend>{t.twoRoom_saunaPositionLabel}</legend>
					<div className="two-room-radio-group">
						<RadioOption
							name="two-room-sauna-position"
							value="left"
							checked={config.saunaPosition === "left"}
							label={t.twoRoom_saunaPositionLeft}
							onChange={(saunaPosition) => onChange({ saunaPosition })}
						/>
						<RadioOption
							name="two-room-sauna-position"
							value="right"
							checked={config.saunaPosition === "right"}
							label={t.twoRoom_saunaPositionRight}
							onChange={(saunaPosition) => onChange({ saunaPosition })}
						/>
					</div>
				</fieldset>
			)}

			<label className="two-room-relax-toggle">
				<input
					type="checkbox"
					checked={config.hasRelaxRoom}
					aria-label={t.twoRoom_relaxToggleAria}
					onChange={(event) =>
						onChange({
							hasRelaxRoom: event.target.checked,
							...(event.target.checked ? {} : { saunaPosition: "left" }),
						})
					}
				/>
				<span>{t.twoRoom_relaxRoomLabel}</span>
			</label>
		</div>
	);
}
